import * as Sentry from '@sentry/vue';
import { endpointKey } from '~/utils/captureApiError';

const SLOW_REQUEST_LOG_MS = 10_000;
const SLOW_REQUEST_ISSUE_MS = 20_000;

export type ReportSlowRequestOptions = {
	method: string;
	schemaPath: string;
	/** Missing on network errors. */
	status?: number;
	durationMs: number;
	/** Part of `durationMs` spent waiting for the Supabase session before the request went out. */
	authMs: number;
	/** The page was hidden at some point while the request ran, so nobody necessarily waited for it. */
	pageHidden: boolean;
};

/** A hanging backend can still answer 200, which no error reporting catches. */
export const reportSlowRequest = ({ method, schemaPath, status, durationMs, authMs, pageHidden }: ReportSlowRequestOptions) => {
	if (durationMs < SLOW_REQUEST_LOG_MS) {
		return;
	}

	const endpoint = endpointKey({ method, schemaPath });
	const details = { method, schemaPath, status, durationMs: Math.round(durationMs), authMs: Math.round(authMs), pageHidden };

	Sentry.logger.warn('[SlowRequest] request slow', details);

	if (durationMs < SLOW_REQUEST_ISSUE_MS || pageHidden) {
		return;
	}

	Sentry.withScope((scope) => {
		scope.setTag('api_endpoint', endpoint);
		scope.setTag('http_status', status ?? 'network-error');
		scope.setTag('slow_phase', authMs > durationMs / 2 ? 'auth' : 'request');
		scope.setContext('Slow Request', details);
		// A stalled tab slows every request at once, so one issue per endpoint only multiplies a single hang.
		scope.setFingerprint(['slow-request']);

		Sentry.captureMessage(`Slow request: took longer than ${SLOW_REQUEST_ISSUE_MS / 1000} s`, 'warning');
	});
};
